import { BASE_AUDIT_API, getHttpErrorMessage, authorizedFetch } from '@/services/api';

// ========== 类型定义 ==========
export interface AttachmentVO {
    id: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    uploadedAt: string; // 后端返回 LocalDateTime 字符串
    fileKey: string;
    // description 字段已删除
}

export interface UploadAttachmentBO {
    targetType: string;
    targetId: number;
    userId: number;
    // description 字段已删除
    companyId?: number;   // 公司ID（用于桶名）
    annualId?: number;    // 年度ID（用于桶名）
}

/** Rich-text HTML notes linked to the same targetType/targetId as attachments */
export interface AttachmentRichtextVO {
    id?: string;
    targetType?: string;
    targetId?: number;
    content?: string;
    companyId?: number;
    annualId?: number;
}

export interface SaveAttachmentRichtextBO {
    targetType: string;
    targetId: number;
    content?: string;
    userId: number;
    companyId?: number;
    annualId?: number;
}
interface BatchUploadResult {
  successCount: number;   // 成功上传的文件数
  errors: string[];       // 失败的错误信息列表
}
// ========== 基础工具 ==========
const base = `${BASE_AUDIT_API}/minio_attachments`;

// 通用请求函数（处理 JSON 响应）
async function request<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await authorizedFetch(path, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...(options?.headers || {}),
        },
    });

    if (!res.ok) {
        throw new Error(await getHttpErrorMessage(res, 'Request failed'));
    }

    const json = await res.json();
    // 假设后端返回格式为 { code: 0, msg: string, data: T }
    if (json.code !== 0) {
        throw new Error(json.msg || 'Unknown error');
    }
    return json.data as T;
}

// ========== API 函数 ==========

/**
 * 上传附件（前端直传）
 * @param file 文件对象
 * @param bo 业务参数（targetType, targetId, userId, companyId, annualId）
 * @returns 附件ID（UUID）
 */
// export async function uploadAttachment(file: File, bo: UploadAttachmentBO): Promise<string> {
//     const BASE_URL = getBaseUrl();
//     const url = `${BASE_URL}${base}/upload-file`;

//     const formData = new FormData();
//     formData.append('file', file);
//     formData.append('bo', new Blob([JSON.stringify(bo)], { type: 'application/json' }));

//     const token = typeof localStorage !== 'undefined'
//         ? (localStorage.getItem('adminToken') || localStorage.getItem('Admin-Token'))
//         : null;

//     const res = await fetch(url, {
//         method: 'POST',
//         credentials: 'include',
//         headers: token ? { Authorization: token } : {},
//         body: formData,
//     });

//     if (!res.ok) {
//         const errorText = await res.text();
//         throw new Error(errorText || 'Upload failed');
//     }

//     const json = await res.json();
//     if (json.code !== 0) {
//         throw new Error(json.msg || 'Upload error');
//     }
//     return json.data as string;
// }

// 移除 uploadAttachment 函数，新增批量上传
/**
 * 批量上传附件
 * @param files 文件列表
 * @param bo 业务参数
 * @returns 包含成功数量和错误列表的结果
 */
export async function batchUploadAttachments(
  files: File[],
  bo: UploadAttachmentBO
): Promise<BatchUploadResult> {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('files', file);
  });
  formData.append('bo', new Blob([JSON.stringify(bo)], { type: 'application/json' }));

  // Must use authorizedFetch so path is ciphered when payload cipher is on.
  // FormData body stays multipart (backend CryptoPayloadFilter skips multipart).
  const res = await authorizedFetch(`${base}/upload-file`, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    throw new Error(await getHttpErrorMessage(res, 'Batch upload failed'));
  }

  const json = await res.json();
  if (json.code !== 0) {
    throw new Error(json.msg || 'Batch upload error');
  }

  const data = json.data as string[];
  const total = files.length;

  // 全部成功：兼容中文原文案 + 后端英文文案（仅扩展识别，不改变既有错误统计逻辑）
  if (
    Array.isArray(data) &&
    data.length === 1 &&
    (data[0] === '全部上传成功' || data[0] === 'All files uploaded successfully')
  ) {
    return { successCount: total, errors: [] };
  }

  // 部分或全部失败：data 中包含错误信息（每条格式如 "文件名 → 错误原因"）
  // 过滤出包含 '→' 的错误行（根据实际后端错误特征）
  const errors = data.filter(item => typeof item === 'string' && item.includes('→'));
  const successCount = total - errors.length;
  return { successCount, errors };
}

/**
 * 获取附件下载链接（MinIO 预签名 URL）。
 * 线上 endpoint 常为内网 `http://minio:9000`，浏览器无法直连；下载请用 {@link downloadAttachmentViaStream}。
 */
export async function getDownloadUrl(
    companyId: number | undefined,
    annualId: number | undefined,
    fileKey: string,
    options?: { inline?: boolean }
): Promise<string> {
    const body = {
        companyId: companyId,
        annualId: annualId,
        fileKeys: [fileKey],
        inline: options?.inline === true ? true : undefined,
    };
    return request<string>(`${base}/download`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/**
 * Fetch attachment bytes via audit → MinIO (no browser→MinIO).
 * Avoids broken links when presigned host is the Docker-internal `minio:9000`.
 */
export async function fetchAttachmentBlob(params: {
    companyId?: number;
    annualId?: number;
    fileKey: string;
}): Promise<Blob> {
    // authorizedFetch: cipher path + JSON body when payload cipher is on.
    // Binary preview responses stay unencrypted (CryptoPayloadFilter only wraps JSON).
    const res = await authorizedFetch(`${base}/preview-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            companyId: params.companyId,
            annualId: params.annualId,
            fileKeys: [params.fileKey],
        }),
    });
    if (!res.ok) {
        throw new Error(await getHttpErrorMessage(res, 'Attachment stream failed'));
    }
    // Gateway crypto errors often return HTTP 200 + JSON Result (e.g. code 4605).
    const ct = (res.headers.get('Content-Type') || '').toLowerCase();
    if (ct.includes('json')) {
        throw new Error(await getHttpErrorMessage(res, 'Attachment stream failed'));
    }
    return res.blob();
}

/** Download one attachment through backend stream (recommended for prod). */
export async function downloadAttachmentViaStream(params: {
    companyId?: number;
    annualId?: number;
    fileKey: string;
    fileName?: string;
}): Promise<void> {
    const blob = await fetchAttachmentBlob(params);
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = params.fileName || params.fileKey || 'attachment';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
}

/**
 * 获取附件列表（根据业务类型和业务ID）
 * @param targetType 业务表名
 * @param targetId 业务记录ID
 * @returns 附件列表
 */
export async function listAttachments(targetType: string, targetId: number): Promise<AttachmentVO[]> {
    const params = new URLSearchParams({ targetType, targetId: String(targetId) });
    return request<AttachmentVO[]>(`${base}/list?${params.toString()}`);
}

/**
 * Load rich-text HTML associated with attachment target (targetType + targetId).
 */
export async function getAttachmentRichText(
    targetType: string,
    targetId: number,
): Promise<AttachmentRichtextVO> {
    const params = new URLSearchParams({ targetType, targetId: String(targetId) });
    return request<AttachmentRichtextVO>(`${base}/rich-text?${params.toString()}`);
}

/**
 * Upsert rich-text HTML associated with attachment target.
 */
export async function saveAttachmentRichText(
    bo: SaveAttachmentRichtextBO,
): Promise<AttachmentRichtextVO> {
    return request<AttachmentRichtextVO>(`${base}/rich-text`, {
        method: 'PUT',
        body: JSON.stringify(bo),
    });
}

/**
 * 软删除附件
 * @param attachmentId 附件ID
 */
export async function deleteAttachment(attachmentId: string): Promise<void> {
    await request<void>(`${base}/delete/${attachmentId}`, {
        method: 'DELETE',
    });
}

/**
 * 批量删除附件
 * @param attachmentIds 附件ID列表
 */
export async function batchDelete(attachmentIds: string[]): Promise<void> {
    await request<void>(`${base}/batchDelete`, {
        method: 'POST',
        body: JSON.stringify({ attachmentIds }),
    });
}



/**
 * 批量下载
 * @param companyId 公司ID
 * @param annualId 年度ID
 * @param fileKeys 文件key数组
 */
export async function batchDownload(
  companyId: number | undefined,
  annualId: number | undefined,
  fileKeys: string[]
): Promise<void> {
  // authorizedFetch: cipher path + JSON body when payload cipher is on.
  // ZIP bytes stay unencrypted (CryptoPayloadFilter only wraps JSON responses).
  const res = await authorizedFetch(`${base}/batchDownload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyId,
      annualId,
      fileKeys,
    }),
  });
  if (!res.ok) {
    throw new Error(await getHttpErrorMessage(res, 'Batch download failed'));
  }
  // Gateway crypto errors often return HTTP 200 + JSON Result (e.g. code 4605).
  const ct = (res.headers.get('Content-Type') || '').toLowerCase();
  if (ct.includes('json')) {
    throw new Error(await getHttpErrorMessage(res, 'Batch download failed'));
  }

  const blob = await res.blob();
  const downloadUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = `附件_${Date.now()}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(downloadUrl);
}

// ========== 便捷导出 ==========
export const attachmentService = {
    // uploadAttachment,
    batchUploadAttachments,
    getDownloadUrl,
    fetchAttachmentBlob,
    downloadAttachmentViaStream,
    listAttachments,
    getAttachmentRichText,
    saveAttachmentRichText,
    deleteAttachment,
    batchDownload,
    batchDelete,
};

/**
 * Resolve preview info for an uploaded lease agreement attachment id / fileKey.
 * Uses backend preview-stream (server→MinIO) to avoid SignatureDoesNotMatch when
 * MinIO endpoint host differs from the host the browser uses.
 */
export type LeaseAgreementPreviewInfo = {
    url: string | null;
    fileName?: string;
    /** image | pdf can embed; other (docx/…) show file name + open link */
    kind: 'image' | 'pdf' | 'other';
};

function detectPreviewKind(fileName?: string, mimeType?: string): LeaseAgreementPreviewInfo['kind'] {
    const mime = String(mimeType || '').toLowerCase();
    const name = String(fileName || '').toLowerCase();
    if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) return 'image';
    if (mime.includes('pdf') || /\.pdf$/i.test(name)) return 'pdf';
    return 'other';
}

async function fetchAttachmentPreviewBlobUrl(params: {
    companyId?: number;
    annualId?: number;
    fileKey: string;
    kind: LeaseAgreementPreviewInfo['kind'];
}): Promise<string> {
    const blob = await fetchAttachmentBlob(params);
    const headerType = (blob.type || '').toLowerCase();
    const type =
        params.kind === 'pdf'
            ? 'application/pdf'
            : params.kind === 'image'
              ? (headerType.startsWith('image/') ? headerType : 'image/*')
              : (headerType || 'application/octet-stream');
    return URL.createObjectURL(new Blob([await blob.arrayBuffer()], { type }));
}

export async function resolveLeaseAgreementPreviewUrl(params: {
    attachmentRef: string;
    targetId: number;
    companyId?: number;
    annualId?: number;
}): Promise<LeaseAgreementPreviewInfo | null> {
    const ref = String(params.attachmentRef || '').trim();
    if (!ref) return null;
    let fileKey = ref;
    let fileName: string | undefined;
    let mimeType: string | undefined;
    try {
        const list = await listAttachments('rental_lease_agreement', params.targetId);
        const hit = (list || []).find((a) => a.id === ref || a.fileKey === ref);
        if (hit?.fileKey) fileKey = hit.fileKey;
        if (hit?.fileName) fileName = hit.fileName;
        if (hit?.mimeType) mimeType = hit.mimeType;
    } catch {
        // use ref as fileKey
    }
    const kind = detectPreviewKind(fileName, mimeType);
    try {
        if (kind === 'image' || kind === 'pdf') {
            const url = await fetchAttachmentPreviewBlobUrl({
                companyId: params.companyId,
                annualId: params.annualId,
                fileKey,
                kind,
            });
            return { url, fileName, kind };
        }
        // Non-embeddable: still stream via backend so Open works when MinIO host is internal
        const url = await fetchAttachmentPreviewBlobUrl({
            companyId: params.companyId,
            annualId: params.annualId,
            fileKey,
            kind: 'other',
        });
        return { url, fileName, kind };
    } catch {
        return { url: null, fileName, kind };
    }
}
